import OpenInNew from "@mui/icons-material/OpenInNew";
import { Alert, Box, Link as MuiLink, Paper, Typography, useTheme } from "@mui/material";
import { InfoField } from "components/InfoField";
import CopyableTypography from "components/Typography/CopyableTypography";
import {
  CohortIdentifierData,
  getIdentifierKeyAndIdentifierFromCohort,
} from "encamp-shared/src/cohorts/identifierUtilities";
import { CohortNameData, generateCohortName } from "encamp-shared/src/cohorts/nameUtilities";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import { prettyPrintDateTime } from "../../../util/dates";
import { useCohortDetails } from "./useCohortDetails";
// Format identifier keys to be more human-readable
function formatIdentifierKey(key: string): string {
  const keyMap: Record<string, string> = {
    fdId: "Fire Department ID",
    lepcId: "LEPC ID",
    credentialUsername: "Credential Username",
    alternateIdValue: "Alternate ID Value",
    state: "State",
    Unknown: "Unknown",
  };

  return keyMap[key] || key;
}

// Format cohort type to handle special cases like LEPC
function formatCohortType(type: string): string {
  if (type === "LEPC") {
    return "LEPC";
  }
  return prettyPrintEnumValue(type);
}

// Format agency address as array of lines for multiline display
function getAgencyAddressLines(agency: {
  mailingAddress?: string | null;
  mailingAddressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string[] {
  const lines: string[] = [];

  // Line 1: Street address
  if (agency.mailingAddress) {
    lines.push(agency.mailingAddress);
  }

  // Line 2: Street address line 2 (if exists)
  if (agency.mailingAddressLine2) {
    lines.push(agency.mailingAddressLine2);
  }

  // Line 3: City, State Zip (standard US format - no comma between state and zip)
  const cityPart = agency.city ?? "";
  const stateZipPart = [agency.state, agency.zip].filter(Boolean).join(" ");
  if (cityPart && stateZipPart) {
    lines.push(`${cityPart}, ${stateZipPart}`);
  } else if (cityPart || stateZipPart) {
    lines.push(cityPart || stateZipPart);
  }

  return lines;
}

export function CohortInfo() {
  const theme = useTheme();
  const { data } = useCohortDetails();

  const cohort = data?.cohort;

  // Check if cohort is blocked due to state denial
  const stateBlockedInfo = useMemo(() => {
    if (!cohort?.members || cohort.members.length === 0) {
      return null;
    }

    // Check the first member's facility state for any denial patterns
    const state = cohort.members[0]?.facility?.state;
    if (!state) return null;

    // Common patterns that indicate state denial - this would need to be customized
    // based on actual business logic for state denials
    const deniedStates = ["IL"]; // Example: Illinois is denied

    if (deniedStates.includes(state)) {
      return {
        isBlocked: true,
        state: state,
        reason: `${state} state regulations do not permit this type of cohort operation`,
      };
    }

    return null;
  }, [cohort?.members]);

  // Get cohort identifier key and value
  const identifierInfo = useMemo(() => {
    if (!cohort) {
      return {
        identifierKey: "Unknown",
        identifier: "Unknown",
      };
    }

    try {
      const minimalCohort: CohortIdentifierData = {
        id: cohort.id,
        type: cohort.type,
        state: cohort.state,
        fdId: cohort.fdId,
        lepcId: cohort.lepcId,
        credentialUsername: cohort.credentialUsername,
        alternateIdValue: cohort.alternateIdValue,
      };

      return getIdentifierKeyAndIdentifierFromCohort({
        cohort: minimalCohort,
      });
    } catch (error) {
      console.error("Error getting cohort identifier:", error);
      return { identifierKey: "Unknown", identifier: "Unknown" };
    }
  }, [cohort]);

  const cohortName = useMemo(() => {
    if (!cohort) {
      return "Unknown Cohort";
    }

    try {
      const nameData: CohortNameData = {
        id: cohort.id,
        type: cohort.type,
        state: cohort.state,
        fdName: cohort.fireDepartment?.name,
        lepcName: cohort.lepc?.name,
        credentialUsername: cohort.credentialUsername,
        alternateIdValue: cohort.alternateIdValue,
        members: cohort.members,
      };

      return generateCohortName(nameData);
    } catch (error) {
      console.error("Error generating cohort name:", error);
      return "Unknown Cohort";
    }
  }, [cohort]);

  if (!cohort) {
    return null;
  }

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
        p: 2,
      }}
      elevation={0}
    >
      <Typography variant="h6" fontWeight="600" sx={{ mb: 1.5, color: theme.palette.text.primary }}>
        Cohort Information
      </Typography>

      {/* State Blocked Warning */}
      {stateBlockedInfo?.isBlocked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            Cohort Blocked: {stateBlockedInfo.reason}
          </Typography>
        </Alert>
      )}

      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            py: 0.5,
            minHeight: "32px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 400,
              minWidth: "180px",
              mr: 2,
            }}
          >
            Cohort Name
          </Typography>
          <CopyableTypography text={cohortName} />
        </Box>
        <InfoField
          label={formatIdentifierKey(identifierInfo.identifierKey)}
          value={identifierInfo.identifier}
        />
        <InfoField label="Type" value={formatCohortType(cohort.type)} />
        {cohort.reportKind != null && (
          <InfoField label="Report Kind" value={prettyPrintEnumValue(cohort.reportKind)} />
        )}
        {cohort.type === "FIRE_DEPARTMENT" && cohort.fireDepartment && (
          <>
            {cohort.fireDepartment.name && (
              <InfoField label="FD Name" value={cohort.fireDepartment.name} />
            )}
            {getAgencyAddressLines(cohort.fireDepartment).length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  py: 0.5,
                  minHeight: "32px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    minWidth: "180px",
                    mr: 2,
                  }}
                >
                  FD Address
                </Typography>
                <CopyableTypography
                  text={getAgencyAddressLines(cohort.fireDepartment).join("\n")}
                  sx={{ whiteSpace: "pre-line" }}
                />
              </Box>
            )}
            {cohort.fireDepartment.email && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  py: 0.5,
                  minHeight: "32px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    minWidth: "180px",
                    mr: 2,
                  }}
                >
                  FD Email
                </Typography>
                <CopyableTypography text={cohort.fireDepartment.email} />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.5,
                minHeight: "32px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  minWidth: "180px",
                  mr: 2,
                }}
              >
                FD Details
              </Typography>
              <MuiLink
                component={Link}
                to={`/staff/agencies/fireDepartments?omnisearch=${encodeURIComponent(`id:${cohort.fireDepartment.id}${cohort.fireDepartment.state ? ` state:${cohort.fireDepartment.state}` : ""} ${cohort.fireDepartment.name ?? ""}`)}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                View in Agencies
                <OpenInNew fontSize="small" />
              </MuiLink>
            </Box>
          </>
        )}
        {cohort.type === "LEPC" && cohort.lepc && (
          <>
            {cohort.lepc.name && <InfoField label="LEPC Name" value={cohort.lepc.name} />}
            {getAgencyAddressLines(cohort.lepc).length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  py: 0.5,
                  minHeight: "32px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    minWidth: "180px",
                    mr: 2,
                  }}
                >
                  LEPC Address
                </Typography>
                <CopyableTypography
                  text={getAgencyAddressLines(cohort.lepc).join("\n")}
                  sx={{ whiteSpace: "pre-line" }}
                />
              </Box>
            )}
            {cohort.lepc.email && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  py: 0.5,
                  minHeight: "32px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    minWidth: "180px",
                    mr: 2,
                  }}
                >
                  LEPC Email
                </Typography>
                <CopyableTypography text={cohort.lepc.email} />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                py: 0.5,
                minHeight: "32px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  minWidth: "180px",
                  mr: 2,
                }}
              >
                LEPC Details
              </Typography>
              <MuiLink
                component={Link}
                to={`/staff/agencies/lepcs?omnisearch=${encodeURIComponent(`id:${cohort.lepc.id}${cohort.lepc.state ? ` state:${cohort.lepc.state}` : ""} ${cohort.lepc.name}`)}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                View in Agencies
                <OpenInNew fontSize="small" />
              </MuiLink>
            </Box>
          </>
        )}
        <InfoField label="Status" value={prettyPrintEnumValue(cohort.status)} />
        <InfoField label="State" value={cohort.members?.[0]?.facility?.state || "Unknown"} />
        <InfoField label="Organization" value={cohort.tenant?.name} />
        <InfoField label="Cohort ID" value={cohort.id} />
        <InfoField label="Created At" value={prettyPrintDateTime(cohort.createdAt)} />
        <InfoField label="Updated At" value={prettyPrintDateTime(cohort.updatedAt)} />
      </Box>
    </Paper>
  );
}
